import React, { useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import classnames from "classnames";
import { AgGridReact } from "ag-grid-react";
import { CellClassParams, ColDef, GridOptions, ICellRendererParams, RowClickedEvent } from "ag-grid-community";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { DefaultAgGridOptions } from "modules/common/services/table/helpers";
import { agGridLocale } from "modules/root/services/locale";
import { QuarterMarginRow, quarterInProgress, quarterNames } from "../models/calc";
import { UnitEconomicsStoreType } from "../models/unit-economics-store";
import { rubSign } from "./format";
import { headerTip } from "./headerTip";
import styles from "./UnitEconomicsPage.module.scss";

type Row = QuarterMarginRow & { pinned?: boolean };
type Params = ICellRendererParams<Row>;

const NameCell = ({ data }: Params) =>
    data ? (
        data.pinned ? (
            <span className={styles.totalName}>{data.name}</span>
        ) : (
            <div className={styles.nameCell}>
                <span className={styles.name} title={data.name}>
                    {data.shortName}
                </span>
                <span className={styles.nameSub}>{data.department}</span>
            </div>
        )
    ) : null;

const MarginCell = ({ value, context, colDef }: Params & { value: number | null; context: QuarterContext }) => {
    const quarter = Number(colDef?.colId?.replace("q", "") || 0);
    if (quarter && quarter > context.lastQuarter) {
        return <span className={styles.muted}>ещё не наступил</span>;
    }

    if (value === null || value === undefined) {
        return (
            <span className={styles.muted} title="Не работал на производственной должности">
                —
            </span>
        );
    }

    return (
        <span className={classnames(styles.num, { [styles.positive]: value > 0, [styles.negative]: value < 0 })}>
            {rubSign(value)}
        </span>
    );
};

interface QuarterContext {
    max: number;
    lastQuarter: number;
    /** Незакрытый квартал (0 — нет такого) */
    current: number;
}

const HEAT_LEVELS = 5;

/**
 * Подложка ячейки: чем больше маржа по модулю, тем насыщеннее зелёный или красный.
 * Пять ступеней насыщенности — классы в стилях на planr-цветах.
 */
const heat = (params: CellClassParams<Row, number | null> & { context: QuarterContext }) => {
    const value = params.value;
    // cellClass заменяет класс выравнивания из type: "rightAligned" — возвращаем его сами
    const classes: string[] = ["ag-right-aligned-cell"];
    if (params.colDef.colId === `q${params.context.current}`) {
        classes.push(styles.inProgress);
    }

    if (params.data?.pinned || value === null || value === undefined || !params.context.max) {
        return classes;
    }

    // корень от доли: иначе один крупный выброс оставляет всех остальных на первой ступени
    const level = Math.max(1, Math.ceil(Math.sqrt(Math.abs(value) / params.context.max) * HEAT_LEVELS));
    classes.push(styles[`heat${value >= 0 ? "Positive" : "Negative"}${level}`]);
    return classes;
};

export const QuarterMarginGrid = observer(({ store, onOpen }: QuarterMarginGridProps) => {
    const rows = store.quarterRows;
    const lastQuarter = store.data ? Math.ceil(store.data.lastMonth / 3) : 4;
    const current = quarterInProgress(store.data);

    const context = useMemo<QuarterContext>(
        () => ({
            max: Math.max(0, ...rows.flatMap((r) => r.quarters.map((q) => Math.abs(q ?? 0)))),
            lastQuarter,
            current,
        }),
        [rows, lastQuarter, current]
    );

    const columns = useMemo<ColDef<Row>[]>(
        () => [
            {
                headerName: "Сотрудник",
                field: "name",
                cellRenderer: NameCell,
                flex: 1,
                minWidth: 260,
                comparator: (a: string, b: string) => a.localeCompare(b),
            },
            ...quarterNames.map<ColDef<Row>>((name, index) => ({
                headerName: current === index + 1 ? `${name} квартал · идёт` : `${name} квартал`,
                ...headerTip(current === index + 1 ? "Квартал ещё не закончился — цифры неполные" : undefined),
                colId: `q${index + 1}`,
                valueGetter: (p) => p.data?.quarters[index] ?? null,
                cellRenderer: MarginCell,
                cellClass: heat as any,
                width: 150,
                type: "rightAligned",
                comparator: (a: number | null, b: number | null) => (a ?? -Infinity) - (b ?? -Infinity),
            })),
            {
                headerName: `Итого за ${store.year}`,
                field: "total",
                cellRenderer: MarginCell,
                width: 160,
                type: "rightAligned",
                sort: "desc",
                cellClass: styles.totalColumn,
            },
        ],
        [store.year, current]
    );

    const options = useMemo<GridOptions>(
        () => ({
            ...DefaultAgGridOptions(true),
            defaultColDef: { cellDataType: false, sortable: true, resizable: true, suppressMovable: true },
        }),
        []
    );

    const totals = useMemo<Row[]>(() => {
        const quarters = [0, 1, 2, 3].map((i) =>
            rows.some((r) => r.quarters[i] !== null) ? rows.reduce((acc, r) => acc + (r.quarters[i] ?? 0), 0) : null
        );

        return [
            {
                id: "total",
                name: `Итого · ${rows.length} чел.`,
                shortName: "",
                department: "",
                quarters,
                total: quarters.reduce<number>((acc, q) => acc + (q ?? 0), 0),
                pinned: true,
            },
        ];
    }, [rows]);

    const onRowClicked = useCallback(
        (e: RowClickedEvent<Row>) => {
            if (e.data && !e.node.rowPinned) {
                onOpen(e.data.id);
            }
        },
        [onOpen]
    );

    return (
        <GridTheme bordered className={`designed ${styles.grid}`}>
            <AgGridReact<Row>
                rowData={rows}
                columnDefs={columns}
                gridOptions={options}
                context={context}
                getRowId={(p) => p.data.id}
                pinnedBottomRowData={rows.length ? totals : []}
                rowHeight={46}
                headerHeight={34}
                onRowClicked={onRowClicked}
                localeText={agGridLocale}
                suppressCellFocus
                enableCellTextSelection
                domLayout="autoHeight"
            />
        </GridTheme>
    );
});

interface QuarterMarginGridProps {
    store: UnitEconomicsStoreType;
    onOpen: (id: string) => void;
}
